import { t } from '@lingui/core/macro'
import type { BaseSensorEntry } from 'types'

import type { AgentModel, ModelSeries } from './useAgentModels'

/**
 * The one model the Agent tab is about, and the chart state it owns.
 *
 * There used to be two stores: one holding the model the agent was assembling
 * from its stream, one holding the model drawn on the chart, and every consumer
 * read `live ?? active` and hoped they agreed. They did not have to, and the
 * comments they accumulated are a history of the ways they came apart. There is
 * one model here, and `source` says where it came from.
 *
 * It is a module store rather than component state because hiding the right
 * panel unmounts the Agent tab, and hiding the panel is exactly what you do to
 * look at the chart. Only an explicit close (or a site change) retires it.
 */

/**
 * A model shown on the chart. Either a saved one or a fit that just landed in
 * the chat — both carry enough to build the view, so a build draws itself
 * before anyone saves it.
 */
export interface ActiveModel extends AgentModel {
  series?: ModelSeries | null
  /** True for a fit that exists only in this conversation. */
  unsaved?: boolean
  /** The site the view belongs to, so a site switch can retire it. */
  siteId?: string
  /** Expression tags the site has no pen for — the KPI line cannot draw. */
  missingForKpi?: string[]
  /** Why the KPI expression itself cannot be charted, if that is the problem. */
  kpiIssue?: string | null
}

/**
 * The id an unsaved build carries.
 *
 * It must be non-empty: `setModelSeries` treats a falsy id as "no model", so an
 * empty string meant the expected and residual columns were never merged for a
 * model the agent had just built — which is every model, until someone saves it.
 */
export const LIVE_MODEL_ID = 'live-model'

export interface ModelCardState {
  model: ActiveModel | null
  /** `live` while the agent is assembling it, `saved` once it is on the chart. */
  source: 'live' | 'saved' | null
  /** The dataset the current build is pulling into. */
  dataset: string | null
  /** Identity of the model already drawn, so a fold does not redraw endlessly. */
  drawn: string | null
  /** The user's pens before we took the chart over, restored on close. */
  penSnapshot: BaseSensorEntry[] | null
  derivedPenId: string | null
  /** Every pen this model put on the chart (KPI inputs and drivers alike). */
  modelPens: BaseSensorEntry[]
  /** Pen ids we hid for the KPI-only view, to restore on exit. */
  hiddenPenIds: string[]
  /** Show only the KPI line and its expectation. */
  kpiOnly: boolean
  /** Draw the saved residual (actual − expected) as its own pen. */
  showResidual: boolean
  /** Derived-value ids this view registered, to retire on close. */
  ownedPenIds: string[]
}

export const EMPTY_CARD: ModelCardState = {
  model: null,
  source: null,
  dataset: null,
  drawn: null,
  penSnapshot: null,
  derivedPenId: null,
  modelPens: [],
  hiddenPenIds: [],
  kpiOnly: false,
  showResidual: false,
  ownedPenIds: [],
}

let state: ModelCardState = EMPTY_CARD
const listeners = new Set<() => void>()
const emit = (): void => listeners.forEach((listener) => listener())

export const subscribeModelCard = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const modelCardState = (): ModelCardState => state

/** The card's model without a React subscription. */
export const getModelCard = (): ActiveModel | null => state.model

export const setModelCardState = (next: ModelCardState): void => {
  state = next
  emit()
}

/**
 * Whether publishing a model KEEPS the pens it does not use.
 *
 * By default a model takes the chart over: its own tags, nothing else, because
 * that is what you want when reading one model. But it means every other pen you
 * had plotted is gone, and getting them back means re-picking them. With this
 * on, the others stay in the list and are merely hidden — so they are one click
 * away in the Settings tab rather than a search away in the tag browser.
 */
let keepOtherPens = false
const keepListeners = new Set<() => void>()

export const setKeepOtherPens = (on: boolean): void => {
  if (keepOtherPens === on) return
  keepOtherPens = on
  keepListeners.forEach((listener) => listener())
}

export const getKeepOtherPens = (): boolean => keepOtherPens

export const subscribeKeepOtherPens = (listener: () => void): (() => void) => {
  keepListeners.add(listener)
  return () => keepListeners.delete(listener)
}

/**
 * What makes a model a DIFFERENT thing to draw.
 *
 * `entry_id` is part of it because saving swaps the placeholder id for the real
 * one, and the chart's expected and residual pens are keyed by it — so a save
 * has to redraw or those pens stay bound to a model that no longer exists.
 */
export const drawIdentity = (model: ActiveModel): string =>
  [
    model.entry_id,
    model.kpi,
    model.expr,
    model.data_through,
    model.last30_gap,
  ].join('|')

/**
 * Take responsibility for drawing this model, once. Both the tab and the dock
 * mount the chart hook, so without a claim they would both publish the same
 * fit and the second would stomp the pens the first had just set. Deliberately
 * silent: claiming is bookkeeping, not a change anyone renders.
 */
export const claimDraw = (identity: string): boolean => {
  if (state.drawn === identity) return false
  state = { ...state, drawn: identity }
  return true
}

/** Consumers register their own teardown, since clearing needs chart context. */
const clearRequested = new Set<() => void>()

export const onClearRequested = (listener: () => void): (() => void) => {
  clearRequested.add(listener)
  return () => clearRequested.delete(listener)
}

/**
 * Wipe the card and give the chart back.
 *
 * Starting a new model used to leave the previous one's parameters on screen,
 * because the two stores emptied independently. Clearing is one action because
 * it is one thing to a reader: "there is no model here".
 */
export const clearModelCard = (): void => {
  setModelCardState({
    ...state,
    model: null,
    source: null,
    dataset: null,
    drawn: null,
  })
  clearRequested.forEach((listener) => listener())
}

const num = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined
const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined

/**
 * Fold one streamed frame into the card. Unknown frames are ignored, so chat
 * text and tool chatter cannot disturb the panel.
 */
export const applyLiveEvent = (frame: Record<string, unknown>): void => {
  const kind = frame.kind

  // A new dataset is NOT a new model. The agent routinely re-pulls the same
  // model into a fresh dataset name — RO_TRAIN_A then ro_model_data — and
  // resetting on that wiped the KPI it had already named, so the save button
  // refused with "the KPI needs a name" about a model that plainly had one.
  // Starting over is an explicit act: the clear button, or a new conversation.
  const dataset = kind === 'roles' ? (str(frame.dataset) ?? null) : null
  if (dataset) state = { ...state, dataset }

  const prev = state.model
  const base: ActiveModel = prev
    ? { ...prev }
    : { entry_id: LIVE_MODEL_ID, name: 'building…', unsaved: true }

  if (kind === 'roles') {
    const roles = (frame.roles ?? {}) as Record<string, string>
    if (!Object.keys(roles).length) return
    // A fresh pull redefines the model's inputs rather than adding to them:
    // the agent re-pulling with a different role map means it changed its mind.
    base.roles = roles
  } else if (kind === 'kpi') {
    const kpi = str(frame.kpi)
    if (!kpi) return
    base.kpi = kpi
    base.expr = str(frame.expr) ?? base.expr
  } else if (kind === 'fit') {
    base.kpi = str(frame.kpi) ?? base.kpi
    base.expr = str(frame.expr) ?? base.expr
    if (frame.roles && Object.keys(frame.roles).length) {
      base.roles = frame.roles as Record<string, string>
    }
    if (Array.isArray(frame.drivers)) base.drivers = frame.drivers as string[]
    base.gate_expr = str(frame.gate_expr) ?? base.gate_expr
    if (Array.isArray(frame.baseline_window)) {
      base.baseline_window = frame.baseline_window as [string, string]
    }
    const extra = base as ActiveModel & {
      baseline_windows?: string[][]
      excluded_ranges?: string[][]
      smoothing_days?: number
      band_source?: string
      row_scale?: number
      buckets?: unknown
    }
    if (Array.isArray(frame.baseline_windows)) {
      extra.baseline_windows = frame.baseline_windows as string[][]
    }
    if (Array.isArray(frame.excluded_ranges)) {
      extra.excluded_ranges = frame.excluded_ranges as string[][]
    }
    extra.smoothing_days = num(frame.smoothing_days) ?? extra.smoothing_days
    extra.band_source = str(frame.band_source) ?? extra.band_source
    extra.row_scale = num(frame.row_scale) ?? extra.row_scale
    if (Array.isArray(frame.buckets)) extra.buckets = frame.buckets
    base.baseline_rows = num(frame.baseline_rows) ?? base.baseline_rows
    base.baseline_median = num(frame.baseline_median) ?? base.baseline_median
    base.err_scale = num(frame.err_scale) ?? base.err_scale
    base.last30_actual = num(frame.last30_actual) ?? base.last30_actual
    base.last30_expected = num(frame.last30_expected) ?? base.last30_expected
    base.last30_gap = num(frame.last30_gap) ?? base.last30_gap
    base.last30_sigma = num(frame.last30_sigma) ?? base.last30_sigma
    base.data_through = str(frame.data_through) ?? base.data_through
    const series = frame.series as ModelSeries | undefined
    if (series?.dates?.length) base.series = series
    base.name = t`${{ value256_21: base.kpi ?? 'KPI' }} (this chat)`
  } else if (kind === 'saved') {
    // Saving ADOPTS the entry, it does not end the build.
    //
    // Dropping the model here meant everything the agent had just decided went
    // with it, so a model it had named, gated and saved came back as "Not saved
    // yet". Keeping the parameters and taking the real id instead is also what
    // makes Apply work — a refit needs an entry to refit.
    const entryId = str(frame.entry_id)
    if (!entryId) return
    base.entry_id = entryId
    base.name = str(frame.name) ?? base.name
    base.unsaved = false
    setModelCardState({ ...state, model: base, source: 'live' })
    return
  } else {
    return
  }

  base.unsaved = true
  setModelCardState({ ...state, model: base, source: 'live' })
}
